import type { ChunkDefinition, ChunkNode, EntityNode, RouteDefinition, RouteEntry, SectionDefinition, SpawnNode } from "../data/chunkTypes";
import { generatedChunks } from "../data/chunks.generated";
import { entityDefinitions, isCollectible, RAMP_SLOPE, trailerDepth } from "../data/entities";
import type { EntityKind, PowerKind, WorldEntity } from "../data/model";
import { generatedRoute } from "../data/route.generated";
import { flight as F, motor as M, route as R, spawning as SP, world as W } from "../data/tuning";

export interface SpawnContext {
  /** the car's current speed (coin arcs match a hop at this speed) */
  speed: number;
  /** flying: no ground pickups, coins go up */
  flying: boolean;
  /** where the flight lands (while flying): road coins from 120 m before it on are kept, the original's preserve range */
  landingZ?: number;
  /** letters/tokens are spawned only when a hunt is on */
  letters: string[];
  seasonTokens: boolean;
}

export type FlightKind = "jetpack" | "headstart";

/** Where a flight comes down: it ends at `start` (everything placed before is behind the car there) over open road to `end`. */
export interface Landing {
  kind: FlightKind;
  start: number;
  end: number;
}

/** our own empty road: the landing section when a route has none, and the open road added after it (see `placeLanding`) */
const OPEN_LANDING: ChunkDefinition = { id: "Landing", cells: 32, nodes: [] };
const OPEN_ROAD: ChunkDefinition = { id: "OpenRoad", cells: 16, nodes: [] };
/** the queue's marker for "the landing section goes here" */
const LANDING = "\u0000landing";

/** stops or trips the car at road level: what a landing must not come down on or in front of */
const inTheWay = (e: WorldEntity): boolean => e.active && !isCollectible(e.kind) && e.kind !== "gate" && e.kind !== "deck"
  && e.kind !== "speedPadBoost" && e.kind !== "speedPadSlow";

export interface WorldOptions {
  random?: () => number;
  chunks?: Record<string, ChunkDefinition>;
  route?: RouteDefinition;
  /** start with the intro chunk (false in tests that place their own entities) */
  intro?: boolean;
  /** the first run gets the forced tutorial chunk */
  tutorial?: boolean;
}


/**
 * The road: chunks placed from a weighted route, entities with absolute positions, SOL and pickups spawned at the
 * chunk's spots, oncoming trucks moving, blockers sliding in, trucks dropping from above, and cleanup behind.
 */
/** The pogo hop's height after `d` metres: an ease-out rise to 32 m over 60 m, then a free fall (the original: a curve up, then gravity). */
export function pogoHeightAt(d: number, speed: number, startY = 0): number {
  const rise = Math.max(0, F.pogoHeight - startY);
  if (d <= F.pogoDistance) { const k = 1 - d / F.pogoDistance; return startY + rise * (1 - k * k); }
  const t = (d - F.pogoDistance) / Math.max(1, speed);
  return Math.max(F.pogoHeight, startY) - 0.5 * M.gravity * t * t;
}

export class World {
  readonly entities: WorldEntity[] = [];
  /** z where the next chunk starts */
  frontier = 0;
  private nextId = 1;
  private random: () => number;
  private chunks: Record<string, ChunkDefinition>;
  private route: RouteDefinition;
  private queue: string[] = [];
  private lastUsed = new Map<string, number>();
  private usedUnique = new Set<string>();
  private rolling = new Map<string, number>();
  private lastPickupZ = -Infinity;
  private pickupCounts: Partial<Record<string, number>> = {};
  /** a flight's landing request, the original's RouteLandingInfo: the landing section goes in at the first section boundary at
   *  or after `z` (where the flight would end) and the flight is stretched to come down on it; `speed` = the landing speed */
  private landingRequest: { kind: FlightKind; z: number; speed: number } | null = null;
  /** the landing of the current flight, once its section is placed (null before, and after a new flight starts) */
  landing: Landing | null = null;
  /** the thrusters' SOL trail in the sky: where the next coin goes (it is laid up to the landing once that is placed) */
  private trail: { z: number; lane: number; y: number; straight: number } | null = null;
  private placed = 0;

  constructor(options: WorldOptions = {}) {
    this.random = options.random ?? Math.random;
    this.chunks = options.chunks ?? generatedChunks;
    this.route = options.route ?? generatedRoute;
    if (options.tutorial && this.chunks.Tutorial_Force) this.queue.push("Tutorial_Force");
    else if (options.intro !== false && this.route.intro) this.queue.push(...this.resolveSection(this.route.intro));
    // dev: ?chunk=<id> puts that chunk (twice) right after the start — screenshots of a given piece of road
    const dev = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV;
    const forced = dev && typeof location !== "undefined" ? new URLSearchParams(location.search).get("chunk") : null;
    if (forced && this.chunks[forced]) this.queue.push(forced, forced);
  }

  /** Builds road until it reaches `spawnAhead` beyond the car. */
  fill(carZ: number, ctx: SpawnContext): void {
    let guard = 0;
    while (this.frontier < carZ + R.spawnAhead && guard++ < 40) this.placeNext(carZ, ctx);
  }

  /**
   * A flight has started and would end at `z`: its landing section (72 m of empty road) goes in at the first section boundary
   * at or after `z` — right after what is already queued when the road there is placed already (the frontier runs 360 m
   * ahead) — and `landing` tells where the flight comes down (the original: RouteLandingInfo → LandingChunkHelper → the flight
   * is stretched to land on it). `speed` is the speed the car will land at: the open road after the touchdown is sized by it.
   */
  scheduleLanding(kind: FlightKind, z: number, speed: number): void {
    this.landing = null;
    this.landingRequest = { kind, z, speed };
    this.queue = this.queue.filter((id) => id !== LANDING);          // a new flight replaces one whose landing is not placed yet
    const queued = this.queue.reduce((sum, id) => sum + (this.chunks[id]?.cells ?? 0) * W.cellDepth, 0);
    if (z <= this.frontier + queued) this.queue.push(LANDING);
  }

  /** a flight is waiting for its landing section to be placed */
  get landingPending(): boolean {
    return this.landingRequest !== null;
  }

  /** Removes obstacles ahead (a continue, a board or shield save). They are marked `cleared`: the scene blows them away. */
  clearAhead(fromZ: number, distance: number): void {
    const cleared = new Set<number>();
    for (const e of this.entities) {
      if (!e.active) continue;
      const def = entityDefinitions[e.kind];
      if (def.clearOnRevive && e.z + e.depth > fromZ - 2 && e.z < fromZ + distance) { e.active = false; e.cleared = true; cleared.add(e.id); }
    }
    // the SOL that lay on what was cleared goes with it (no coins left hanging over an empty road)
    for (const e of this.entities) {
      if (!e.active || e.kind !== "coin") continue;
      if ((e.rideOn !== undefined && cleared.has(e.rideOn)) || (e.yBase > 2.5 && e.z > fromZ - 2 && e.z < fromZ + distance)) e.active = false;
    }
  }

  /** Where an oncoming truck's front is when the car is at `carZ`: it meets the car at `meet`, closing at speedFactor × the car's pace. */
  moverZ(e: WorldEntity, carZ: number): number {
    return (e.meet ?? e.z) + (e.speedFactor ?? 1) * ((e.meet ?? e.z) - carZ);
  }

  update(dt: number, carZ: number, carX: number): void {
    const movers = new Map<number, WorldEntity>();
    for (const e of this.entities) {
      if (e.active && e.meet !== undefined) { e.z = this.moverZ(e, carZ); movers.set(e.id, e); }
    }
    for (const e of this.entities) {
      if (!e.active) continue;
      if (e.rideOn !== undefined) {
        const truck = movers.get(e.rideOn);
        if (truck) e.z = truck.z + (e.rideOffset ?? 0); else e.rideOn = undefined;
      }
      if (e.react && e.react.state !== "done") {
        const ahead = e.z - carZ;
        if (e.react.state === "idle" && ahead < e.react.hint) e.react.state = "hinting";
        if (ahead < e.react.distance) {
          e.react.state = "done";
          if (e.react.kind === "slide") { e.x = e.lane * W.laneWidth; }
          if (e.react.kind === "vanish") e.active = false;
        }
      }
      if (e.z + e.depth < carZ - R.cleanupBehind) e.active = false;
    }
    void carX;
    if (this.entities.length > 1200 && this.placed % 4 === 0) this.compact();
  }

  /**
   * SOL in the sky along a thruster flight, the original's JetpackCoinSpawner: a coin every 6 m, a path point every
   * 3 coins in a random lane, the coins between them weaving from lane to lane — you steer side to side to get them all.
   */
  spawnJetpackCoins(fromZ: number, length: number, height: number, flightSpeed: number): void {
    // the original's JetpackSpawnerCoinInstance: from the end of the climb + 40 m, starting in the middle lane,
    // three coins straight, then a change of -1/0/+1 lane with one coin halfway between, until the landing
    // (a second thruster pick-up mid-flight carries the trail on)
    const start = fromZ + flightSpeed * F.jetpackClimbSeconds + F.jetpackCoinsStart;
    if (!this.trail || this.trail.z < start - F.jetpackCoinSpacing * 6) this.trail = { z: start, lane: 0, y: height + F.jetpackCoinYOffset, straight: 0 };
    this.extendJetpackCoins(fromZ + length);
  }

  /** Lays the thrusters' trail on up to `end` (a flight stretched to its landing gets SOL all the way). */
  private extendJetpackCoins(end: number): void {
    const t = this.trail;
    if (!t) return;
    while (t.z < end) {
      if (t.straight < 3) { this.add("coin", t.lane, t.z, t.y); t.z += F.jetpackCoinSpacing; t.straight += 1; continue; }
      const next = Math.max(-1, Math.min(1, t.lane + Math.floor(this.random() * 3) - 1));
      if (next !== t.lane) { this.add("coin", (t.lane + next) / 2, t.z, t.y); t.z += F.jetpackCoinSpacing; }
      t.lane = next;
      t.straight = 0;
    }
  }

  spawnPogoCoins(fromZ: number, speed: number, startY = 0): void {
    // rows of three along the hop: up the 60 m rise, then down the free fall
    const fall = Math.sqrt((2 * F.pogoHeight) / M.gravity) * Math.max(1, speed);
    const start = fromZ + F.pogoCoinStart;
    const end = fromZ + F.pogoDistance + fall * 0.85;
    for (let i = 0; i < F.pogoCoinRows; i++) {
      const z = start + ((end - start) * i) / (F.pogoCoinRows - 1);
      const y = Math.max(0.6, pogoHeightAt(z - fromZ, speed, startY));
      for (const lane of [-1, 0, 1]) this.add("coin", lane, z, y + F.pogoCoinYOffset);
    }
  }

  /** Entities overlapping a lane in a z range (for tests and for the scene). */
  near(carZ: number, range: number): WorldEntity[] {
    return this.entities.filter((e) => e.active && e.z < carZ + range && e.z + e.depth > carZ - 20);
  }

  // ------------------------------------------------------------------------------------------------- placement

  private placeNext(carZ: number, ctx: SpawnContext): void {
    if (this.queue.length === 0) {
      // a section boundary: a flight's landing section goes in here once the flight would have ended (the original's queue)
      if (this.landingRequest && this.landingRequest.z <= this.frontier) this.queue.push(LANDING);
      else this.queue.push(...this.pickSection(this.frontier));
    }
    const id = this.queue.shift();
    if (id === LANDING) { this.placeLanding(carZ, ctx); return; }
    const def = (id && this.chunks[id]) || (this.route.fallback && this.chunks[this.resolveSection(this.route.fallback)[0] ?? ""]) || Object.values(this.chunks)[0];
    if (!def) return;
    this.placeAtFrontier(def, carZ, ctx);
  }

  private placeAtFrontier(def: ChunkDefinition, carZ: number, ctx: SpawnContext): void {
    this.placeChunk(def, this.frontier, ctx, carZ);
    this.frontier += def.cells * W.cellDepth;
    this.placed += 1;
  }

  /**
   * The landing: the route's landing section (72 m of empty road) at the frontier. The flight ends where everything placed
   * before is behind the car — the section's start, or past a long truck reaching into it or an oncoming one still alongside.
   * Ours on top (the original has no such guard): empty road is added until it covers the fall from the flight's height plus
   * `landingReactionSeconds` at the landing speed — the 50 s speed ramp makes top speed common, and there the original's
   * 72 m left 0.66 s after the touchdown.
   */
  private placeLanding(carZ: number, ctx: SpawnContext): void {
    const request = this.landingRequest;
    if (!request) return;
    this.landingRequest = null;
    const start = this.frontier;
    let from = start;
    for (const e of this.entities) {
      if (!inTheWay(e)) continue;
      // an oncoming truck has passed the car once its back is behind it: it meets the car at `meet` and closes at (1 + s) × the pace
      const clear = e.meet !== undefined ? e.meet + e.depth / (1 + (e.speedFactor ?? 1)) : e.z + e.depth;
      if (clear > from - 1) from = clear + 1;
    }
    const section = request.kind === "jetpack" ? this.route.jetpackLanding : this.route.headstartLanding;
    const defs = (section ? this.resolveSection(section) : []).map((id) => this.chunks[id]).filter((d): d is ChunkDefinition => !!d && d.nodes.length === 0);
    for (const def of defs.length ? defs : [OPEN_LANDING]) this.placeAtFrontier(def, carZ, ctx);
    const fall = Math.sqrt((2 * Math.max(F.jetpackHeight, F.headstartHeight)) / M.gravity);
    const need = request.speed * (fall + F.landingReactionSeconds);
    while (this.frontier - from < need) this.placeAtFrontier(OPEN_ROAD, carZ, ctx);
    this.landing = { kind: request.kind, start: from, end: this.frontier };
    if (this.trail) { this.extendJetpackCoins(from); this.trail = null; }
  }

  private pickSection(z: number): string[] {
    const valid: RouteEntry[] = this.route.entries.filter((e) =>
      e.weight > 0 && z >= e.zMin && (e.zMax === null || z < e.zMax)
      && !(e.unique && this.usedUnique.has(e.section.id))
      && (this.lastUsed.get(e.section.id) === undefined || z - (this.lastUsed.get(e.section.id) ?? 0) >= this.route.minimumRepeatDistance));
    let pool = valid;
    if (pool.length === 0) {
      pool = this.route.entries.filter((e) => z >= e.zMin && (e.zMax === null || z < e.zMax));
      if (pool.length === 0 && this.route.fallback) return this.resolveSection(this.route.fallback);
    }
    const top = Math.max(...pool.map((e) => e.priority));
    pool = pool.filter((e) => e.priority === top);
    const total = pool.reduce((sum, e) => sum + Math.max(1, e.weight), 0);
    let pick = this.random() * total;
    let chosen = pool[pool.length - 1];
    for (const e of pool) {
      pick -= Math.max(1, e.weight);
      if (pick <= 0) { chosen = e; break; }
    }
    if (!chosen) return this.route.fallback ? this.resolveSection(this.route.fallback) : [];
    this.lastUsed.set(chosen.section.id, z);
    if (chosen.unique) this.usedUnique.add(chosen.section.id);
    return this.resolveSection(chosen.section);
  }

  /** The original's section kinds: one · sequence · all · rolling · alternating · composite. */
  private resolveSection(section: SectionDefinition): string[] {
    const chunks = (section.chunks ?? []).filter((id) => this.chunks[id]);
    switch (section.kind) {
      case "composite":
        return (section.parts ?? []).flatMap((part) => this.resolveSection(part));
      case "sequence":
      case "deck":
        return chunks;
      case "all": {
        const copy = [...chunks];
        for (let i = copy.length - 1; i > 0; i--) { const j = Math.floor(this.random() * (i + 1)); [copy[i], copy[j]] = [copy[j], copy[i]]; }
        return copy;
      }
      case "rolling":
      case "alternating": {
        if (chunks.length === 0) return [];
        const i = (this.rolling.get(section.id) ?? -1) + 1;
        this.rolling.set(section.id, i);
        return [chunks[i % chunks.length]];
      }
      case "one":
      default:
        return chunks.length ? [chunks[Math.floor(this.random() * chunks.length)]] : [];
    }
  }

  private currentChunk = "";

  private placeChunk(def: ChunkDefinition, baseZ: number, ctx: SpawnContext, carZ: number): void {
    this.currentChunk = def.id;
    const place = (nodes: ChunkNode[], mirrored: boolean, shift: number): void => {
      for (const node of nodes) {
        if (node.t === "e") this.placeEntity(node, baseZ, mirrored, carZ, shift);
        else if (node.t === "s") this.placeSpawn(node, baseZ, mirrored, ctx, shift);
        else if (node.t === "mirror") place(node.c[0] ?? [], mirrored !== this.random() < (node.p ?? 0.5), shift);
        else if (node.t === "maybe") { if (this.random() < (node.p ?? 0.5)) place(node.c[0] ?? [], mirrored, shift); }
        else if (node.t === "lane") {
          const lanes = node.lanes?.length ? node.lanes : [node.base ?? 0];
          const lane = lanes[Math.floor(this.random() * lanes.length)];
          place(node.c[0] ?? [], mirrored, shift + lane - (node.base ?? 0));
        } else if (node.t === "one") {
          if (node.p !== undefined && this.random() >= node.p) continue;
          const option = this.pickWeighted(node.c, node.w);
          if (option) place(option, mirrored, shift);
        }
      }
    };
    place(def.nodes, false, 0);
  }

  private pickWeighted<T>(options: T[], weights?: number[]): T | undefined {
    if (!weights || weights.length !== options.length) return options[Math.floor(this.random() * options.length)];
    const total = weights.reduce((a, b) => a + Math.max(0, b), 0);
    if (total <= 0) return options[Math.floor(this.random() * options.length)];
    let r = this.random() * total;
    for (let i = 0; i < options.length; i++) { r -= Math.max(0, weights[i]); if (r <= 0) return options[i]; }
    return options[options.length - 1];
  }

  private placeEntity(node: EntityNode, baseZ: number, mirrored: boolean, carZ: number, shift = 0): void {
    const lane = (mirrored ? -1 : 1) * (node.lane + shift);
    const z = baseZ + node.z;
    if (z < carZ + 8) return;                                   // never drop something onto the car
    switch (node.k) {
      case "truck":
      case "mover": {
        const n = node.n ?? 3;
        const e = this.add(node.k, lane, z, 0, { depth: trailerDepth(n), trailers: n });
        if (node.k === "mover") {
          e.meet = z;
          e.speedFactor = node.s ?? 1;
          e.z = this.moverZ(e, carZ);
        }
        if (node.r) e.react = { kind: node.r.kind, hint: node.r.hint, distance: node.r.dist, state: "idle" };
        if (node.r?.kind === "fall") e.stumble = "hard";          // the original's FallingTrain: a crash-type obstacle
        for (const ride of node.rides ?? []) {
          const count = ride.p === "line" ? ride.n ?? 6 : 1;
          const length = ride.p === "line" ? ride.len ?? SP.coinSpacing * (count - 1) : 0;
          for (let i = 0; i < count; i++) {
            const offset = ride.z - length / 2 + (count > 1 ? (i * length) / (count - 1) : 0);
            const c = this.add("coin", lane, e.z + offset, ride.y);
            c.rideOn = e.id;
            c.rideOffset = offset;
          }
        }
        return;
      }
      case "gate": {
        // the original's variants by which lanes stay open under the deck: Mid = the middle, Sides = left + right,
        // Left = only the left, Right = only the right (its collider_left/mid/right walls fill the rest)
        const variant = node.v ?? "mid";
        const side = mirrored ? -1 : 1;
        const blocked = variant === "mid" ? [-1, 1] : variant === "sides" ? [0] : variant === "left" ? [0, side] : [-side, 0];
        this.add("deck", 0, z, entityDefinitions.deck.yBase);
        this.add("gate", 0, z, 0, { width: 12, depth: 25 });
        for (const l of blocked) this.add("gateWall", l, z, 0);
        return;
      }
      case "platform":
        this.add("platform", lane, z, 0, { depth: node.depth ?? 9, height: node.h ?? 1.2 });
        return;
      default: {
        if (node.k === "barrierLow" && !node.r && this.random() < this.ethChance(z) * 0.9) {
          this.add("ethCoin", lane, z, 0);
          return;
        }
        const e = this.add(node.k, lane, z, entityDefinitions[node.k].yBase);
        if (node.r) {
          e.react = { kind: node.r.kind, hint: node.r.hint, distance: node.r.dist, state: "idle", slideFrom: lane };
          if (node.r.kind === "slide") e.x = (lane + (lane <= 0 ? -1 : 1)) * W.laneWidth;   // waits beside the lane, slides in
        }
      }
    }
  }

  private placeSpawn(node: SpawnNode, baseZ: number, mirrored: boolean, ctx: SpawnContext, shift = 0): void {
    const lane = (mirrored ? -1 : 1) * (node.lane + shift);
    const z = baseZ + node.z;
    const y = node.y;
    if (ctx.flying && node.g === "coin" && (ctx.landingZ === undefined || z < ctx.landingZ - F.preserveBeforeLanding)) return;
    switch (node.p) {
      case "single":
        this.add("coin", lane, z, y);
        return;
      case "line": {
        // a line is centred on its spawn point ("6 over 150" on a truck roof fills the roof)
        const n = node.n ?? 6;
        const length = node.len ?? SP.coinSpacing * (n - 1);
        const step = n > 1 ? length / (n - 1) : 0;
        const z0 = z - length / 2;
        // the ETH trap: on the road (not on roofs), one coin in a longer line is an ETH coin — hop it or trip
        const trap = n >= 4 && y < 1.5 && Number.isInteger(lane) && this.random() < this.ethChance(z) ? Math.floor(n / 2) : -1;
        for (let i = 0; i < n; i++) {
          if (i === trap) this.add("ethCoin", lane, z0 + i * step, 0);
          else this.add("coin", lane, z0 + i * step, y);
        }
        return;
      }
      case "curve": {
        // SOL along a hop at the current speed; `scale` says where the obstacle sits in the arc (0.5 = under the peak)
        // the original's CurveSpawner scales with speed: int((7 − 0.5) × speed ratio) + 1 coins (7 → 14) over a hop's length
        const airtime = 2 * Math.sqrt(2 * M.jumpHeight / M.gravity);
        const speed = Math.max(M.startSpeed, ctx.speed);
        const length = airtime * speed;
        const count = Math.floor((SP.curveCoins - 0.5) * (speed / M.startSpeed)) + 1;
        const start = z - (node.scale ?? 0.5) * length;
        for (let i = 0; i < count; i++) {
          const t = i / (count - 1);
          this.add("coin", lane, start + t * length, y + M.jumpHeight * 0.85 * 4 * t * (1 - t));
        }
        return;
      }
      case "pogo":
        this.add("pickup", lane, z, y, { power: "pogo" });
        return;
      case "pickup":
        this.placePickup(lane, z, y, node.g, ctx);
        return;
    }
  }

  /** ETH coins get more common with distance: 12 % of the chances at the start, 35 % from 2 km. */
  private ethChance(z: number): number {
    return Math.min(0.35, 0.12 + z / 8700);
  }

  /** A pickup spot: sometimes a power-up, a key, a box or a letter; otherwise empty — rarer early, common after 800 m. */
  private placePickup(lane: number, z: number, y: number, group: SpawnNode["g"], ctx: SpawnContext): void {
    if (ctx.flying) return;
    if (z - this.lastPickupZ < SP.minDistanceBetweenPickups) return;
    const rarity = Math.min(1, Math.pow(z / R.rarityMaxDistance, 1 / R.raritySteepness));
    const chance = (group === "special" ? 0.6 : group === "medium" ? 0.45 : 0.35) * (0.35 + 0.65 * rarity);
    if (this.random() >= chance) return;
    const weights: Record<string, number> = { ...SP.weights };
    if ((this.pickupCounts.key ?? 0) >= SP.maxKeysPerRun) weights.key = 0;
    if ((this.pickupCounts.mysteryBox ?? 0) >= SP.maxBoxesPerRun) weights.mysteryBox = 0;
    if (ctx.letters.length === 0) weights.letter = 0;
    if (!ctx.seasonTokens) weights.token = 0;
    const total = Object.values(weights).reduce((a, b) => a + b, 0);
    let pick = this.random() * total;
    let chosen = "magnet";
    for (const [k, w] of Object.entries(weights)) { pick -= w; if (pick <= 0) { chosen = k; break; } }
    this.lastPickupZ = z;
    this.pickupCounts[chosen] = (this.pickupCounts[chosen] ?? 0) + 1;
    if (chosen === "key") this.add("key", lane, z, y);
    else if (chosen === "mysteryBox") this.add("mysteryBox", lane, z, y);
    else if (chosen === "letter") this.add("letter", lane, z, y, { letter: ctx.letters[Math.floor(this.random() * ctx.letters.length)] });
    else if (chosen === "token") this.add("token", lane, z, y);
    else this.add("pickup", lane, z, y, { power: chosen as PowerKind });
  }

  /** Parked trucks and ramps placed lately: a coin that lands inside one (a long truck reaching into the next section) goes onto its roof. */
  private surfaces: WorldEntity[] = [];

  private liftOntoRoof(coin: WorldEntity): void {
    for (const s of this.surfaces) {
      if (!s.active || Math.abs(s.x - coin.x) > s.width / 2) continue;
      if (coin.z < s.z || coin.z > s.z + s.depth) continue;
      const top = s.kind === "ramp" ? s.yBase + s.height * Math.min(1, (coin.z - s.z) / RAMP_SLOPE) : s.yBase + s.height;
      if (coin.yBase < top - 0.2) coin.yBase = top + SP.coinHeight;
    }
  }

  add(kind: EntityKind, lane: number, z: number, y: number, extra: Partial<WorldEntity> = {}): WorldEntity {
    const def = entityDefinitions[kind];
    const e: WorldEntity = {
      id: this.nextId++, kind, lane, x: lane * W.laneWidth, z, depth: def.depth, width: def.width, yBase: y, height: def.height,
      speedZ: 0, trailers: 1, active: true, counted: false, src: this.currentChunk, ...extra
    };
    this.entities.push(e);
    if ((kind === "truck" || kind === "ramp") && e.meet === undefined) {
      this.surfaces.push(e);
      if (this.surfaces.length > 40) this.surfaces.shift();
    }
    if (kind === "coin" && e.rideOn === undefined && !extra.rideOn) this.liftOntoRoof(e);
    return e;
  }

  private compact(): void {
    const alive = this.entities.filter((e) => e.active);
    this.entities.splice(0, this.entities.length, ...alive);
  }
}
